import { Injectable, inject } from "@angular/core";
import { catchError, map, switchMap, takeWhile, withLatestFrom } from "rxjs";

import { Actions, createEffect, ofType } from "@ngrx/effects";
// Store
import { Store } from "@ngrx/store";
import * as SportsbookActions from "../../../stores/sportsbook/sportsbook.actions";
import { SportsbookFacade } from "../../../stores/sportsbook/sportsbook.facade";
import { SportsbookState } from "../../../stores/sportsbook/sportsbook.reducers";

// Services
import { BufferFacade } from "@shared-providers/facades";
import { SportTrackPollingService } from "@sports-services/index";
import { SPORTS_NAVIGATION_SERVICE_ERROR_CODES } from "@sports-services/sports.navigation.service";

// Utils
import { SportLiveService } from "@sports-services/sports.live.service";
import { SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION } from "@sports-services/sports.navigation.service/sports.navigations.constants";
import { livePageUtils } from "../../../../sports/utils/livePageUtils.utils";
import { SportHandleList } from "../../../../sports/utils/sport-handle-list";

@Injectable({ providedIn: "root" })
export class SportsbookLiveEffects {
  // Services
  actions$ = inject(Actions);
  bufferFacade = inject(BufferFacade);
  sportLiveService = inject(SportLiveService);

  // Utils
  livePageUtils = inject(livePageUtils);
  store = inject(Store<SportsbookState>);
  sportsbookFacade = inject(SportsbookFacade);
  sportTrackPollingService = inject(SportTrackPollingService);

  timer = {
    live: "live",
    regular: "regular",
    highlights: "highlights",
  };

  // LIVE
  initLive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.initLive),
      switchMap(() => {
        this.sportTrackPollingService.stopAllPollings();
        return this.sportLiveService.getLiveEventsAndSportsBySportHandle().pipe(
          map((data) => {
            const sportsNav = this.livePageUtils.getSportNav(data["SportHandleList"]);
            let selectedSportsNav = sportsNav.find((sport) => sport.SportHandle === SportHandleList.soccer);
            if (!selectedSportsNav && sportsNav?.length) {
              selectedSportsNav = sportsNav[0];
            }
            const orderedArrayEventsBySportHandle = this.livePageUtils.getOrderedEvents(
              sportsNav,
              data.Events,
              selectedSportsNav,
            );
            const categories = this.livePageUtils.getCategories(orderedArrayEventsBySportHandle, data.SportHandle);
            const selectedCategory = categories[0];
            const sport = this.livePageUtils.getSport(data, orderedArrayEventsBySportHandle);
            if (!categories.length)
              return SportsbookActions.setSportsError({
                errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.EMPTY_DATA_ERROR,
                errorDescription: SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION.LOCATION_7,
              });
            return SportsbookActions.setLiveAndFetch({
              sportsNav: sportsNav,
              selectedSportsNav: selectedSportsNav,
              categories: categories,
              selectedCategory: selectedCategory,
              sport: sport,
            });
          }),
          catchError((e) => {
            this.sportsbookFacade.setSportsError({
              errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.GENERIC_ERROR,
              errorDescription: SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION.LOCATION_8,
            });
            return [];
          }),
        );
      }),
    ),
  );

  setLiveAndFetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.setLiveAndFetch),
      map((livePageData) => {
        return SportsbookActions.fetchLive({
          sportsNav: livePageData.sportsNav,
          selectedSportsNav: livePageData.selectedSportsNav,
          categories: livePageData.categories,
          selectedCategory: livePageData.selectedCategory,
          sport: livePageData.sport,
        });
      }),
    ),
  );

  setSelectedSportsNav$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.setSelectedSportsNav),
      withLatestFrom(this.store.select("sportsbook")),
      map(([newSelectedSportsNav, sportsbook]) => {
        this.sportTrackPollingService.stopAllPollings();
        const livePageData = sportsbook["liveData"];
        const selectedSportsNav = livePageData.sportsNav.find(
          (i) => i.SportHandle === newSelectedSportsNav.SportHandle,
        );
        return SportsbookActions.fetchLive({
          ...livePageData,
          selectedSportsNav,
        });
      }),
      catchError((e) => {
        this.sportsbookFacade.setSportsError({
          errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.GENERIC_ERROR,
          errorDescription: SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION.LOCATION_9,
        });
        return [];
      }),
    ),
  );

  fetchLive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.fetchLive),
      switchMap(({ type, ...payload }) => {
        this.sportTrackPollingService.startLivePagePolling();
        return this.bufferFacade
          .newAuxBuffer(
            this.sportLiveService,
            "getLiveEventsAndSportsBySportHandle",
            payload.selectedSportsNav.SportHandle,
            this.timer.live,
            this.sportTrackPollingService.livePolling$,
          )
          .pipe(
            withLatestFrom(this.store.select("sportsbook")),
            takeWhile((_) => this.sportTrackPollingService.livePolling$.getValue()),
            map(([[prev, current], sportsbook]) => {
              const lastLivePageData = sportsbook["liveData"];
              const sportsNav = this.livePageUtils.getSportNav(current["SportHandleList"]);
              const selectedSportsNav = sportsNav.find(
                (snItem) => snItem.SportHandle === lastLivePageData.selectedSportsNav.SportHandle,
              );
              const eventsWithOddsUpdated = this.livePageUtils.getOddsUpdated(current.Events, prev?.Events);
              const orderedArrayEventsBySportHandle = this.livePageUtils.getOrderedEvents(
                sportsNav,
                eventsWithOddsUpdated,
                selectedSportsNav,
              );
              const categories = this.livePageUtils.getCategories(
                orderedArrayEventsBySportHandle,
                current.SportHandle,
              );
              let selectedCategory = categories.find(
                (cat) => cat.CategoryId === lastLivePageData.selectedCategory.CategoryId,
              );
              if (!selectedCategory) {
                selectedCategory = categories[0];
                console.log("Selected category is not present in the latest received categories");
              }
              const sport = this.livePageUtils.getSport(current, orderedArrayEventsBySportHandle);
              // if (!categories.categories.length)
              //   return SportsbookActions.setSportsError({
              //     errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.EMPTY_DATA_ERROR,
              //     errorDescription: "Empty categories in FetchLive effect",
              //   });
              return SportsbookActions.setLive({
                sportsNav: sportsNav,
                selectedSportsNav: selectedSportsNav,
                categories: categories,
                selectedCategory: selectedCategory,
                sport: sport,
              });
            }),
            catchError((e) => {
              this.sportsbookFacade.setSportsError({
                errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.GENERIC_POLLING_ERROR,
                errorDescription: SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION.LOCATION_10,
              });
              return [];
            }),
          );
      }),
    ),
  );
}
